import { orderXClient } from '@/service/client'
import { unwrapServerMessage } from '@/service/server-message'
import { TradeVenue } from '@/types/generated/common/types'
import {
  BybitOauthMintStateResponse,
  DeribitOauthMintStateResponse,
  DeriveConfirmSessionKeyRequest,
  DeriveDisconnectRequest,
  DeriveLinkSubaccountsRequest,
  DeriveLinkSubaccountsResponse,
  DeriveListSubaccountsRequest,
  DeriveListSubaccountsResponse,
  DerivePrepareSessionKeyRequest,
  DerivePrepareSessionKeyResponse,
  HyperliquidConfirmAgentRequest,
  HyperliquidDisconnectAgentRequest,
  HyperliquidLinkSubaccountsRequest,
  HyperliquidLinkSubaccountsResponse,
  HyperliquidListSubaccountsRequest,
  HyperliquidListSubaccountsResponse,
  HyperliquidPrepareAgentRequest,
  HyperliquidPrepareAgentResponse,
  LighterConfirmIntegratorConsentRequest,
  LighterConfirmIntegratorConsentResponse,
  LighterConfirmSubaccountKeyRequest,
  LighterConfirmSubaccountKeyResponse,
  LighterLinkSubaccountsRequest,
  LighterLinkSubaccountsResponse,
  LighterListSubaccountsRequest,
  LighterListSubaccountsResponse,
  LighterPrepareIntegratorConsentRequest,
  LighterPrepareIntegratorConsentResponse,
  LighterPrepareSubaccountKeyRequest,
  LighterPrepareSubaccountKeyResponse,
  PapertradeConfirmSessionKeyRequest,
  PapertradeDisconnectRequest,
  PapertradePrepareSessionKeyRequest,
  PapertradePrepareSessionKeyResponse,
  ParadigmConnectDeskRequest,
  ParadigmConnectDeskResponse,
  ParadigmDisconnectRequest,
  ParadigmLinkAccountRequest,
  ParadigmLinkAccountResponse,
  ParadigmListAccountsRequest,
  ParadigmListAccountsResponse,
  ServerMessage,
  Success,
  VenueList,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export async function fetchVenues(): Promise<VenueList> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getVenues: {} })
    return data.data?.venues ?? { venues: [] }
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Mint a single-use CSRF state nonce for an exchange OAuth popup. Bound to
 * (user, account, venue) and expires after 5 minutes — call this right before
 * opening the popup, never at page load.
 */
export async function mintOauthState(
  venue: TradeVenue,
  payload: { accountId: string }
): Promise<{ state: string }> {
  if (venue !== TradeVenue.TRADE_VENUE_DERIBIT && venue !== TradeVenue.TRADE_VENUE_BYBIT) {
    throw new Error('OAuth is not supported for this venue.')
  }
  try {
    if (venue === TradeVenue.TRADE_VENUE_DERIBIT) {
      const { data } = await orderXClient.post<ServerMessage>('/api', {
        deribitOauthMintState: payload,
      })
      return data.deribitOauthMintStateResponse as DeribitOauthMintStateResponse
    }
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      bybitOauthMintState: payload,
    })
    return data.bybitOauthMintStateResponse as BybitOauthMintStateResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/** Exchange the one-time OAuth `code` for a server-side credential link. */
export async function exchangeOauthCode(
  venue: TradeVenue,
  payload: { accountId: string; code: string; state: string; redirectUri: string }
): Promise<Success> {
  if (venue !== TradeVenue.TRADE_VENUE_DERIBIT && venue !== TradeVenue.TRADE_VENUE_BYBIT) {
    throw new Error('OAuth is not supported for this venue.')
  }
  try {
    if (venue === TradeVenue.TRADE_VENUE_DERIBIT) {
      const { data } = await orderXClient.post<ServerMessage>('/api', {
        deribitOauthExchangeCode: payload,
      })
      if (data.error) return Promise.reject(data.error)
      return unwrapServerMessage(data)
    }
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      bybitOauthExchangeCode: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Mint a Hyperliquid agent (API) wallet and return the EIP-712 payloads the
 * customer's own wallet signs. Single-use, expires after 10 minutes — call this
 * immediately before prompting the wallet, never at page load.
 *
 * `signatureChainId` is the wallet's active EVM chain id (from `eth_chainId`);
 * the backend stamps it into `domain.chainId` of the returned payloads (and the
 * action's signatureChainId) so MetaMask signs without forcing a network switch.
 * Unset/0 falls back to the backend default — always send it. `masterAddress`
 * lets the backend pre-check "already linked to another account" before the
 * user signs, instead of only discovering it at confirm time.
 */
export async function hyperliquidPrepareAgent(
  payload: HyperliquidPrepareAgentRequest
): Promise<HyperliquidPrepareAgentResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      hyperliquidPrepareAgent: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.hyperliquidPrepareAgentResponse as HyperliquidPrepareAgentResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Relay the customer's approveAgent (and approveBuilderFee) signatures. The
 * backend submits them to Hyperliquid and activates the account on success.
 * Errors (deposit-first / session-expired / invalid-signature) surface verbatim.
 */
export async function hyperliquidConfirmAgent(
  payload: HyperliquidConfirmAgentRequest
): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      hyperliquidConfirmAgent: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Owner-gated unlink of a Hyperliquid agent wallet. The backend clears the
 * stored agent credentials, flips the account to Inactive, and releases the
 * master-wallet reservation so the wallet can be relinked elsewhere. The agent
 * is left to expire on Hyperliquid (not deregistered). Errors surface verbatim.
 */
export async function hyperliquidDisconnectAgent(
  payload: HyperliquidDisconnectAgentRequest
): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      hyperliquidDisconnectAgent: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * List a Hyperliquid wallet's sub-accounts for the connection picker.
 * Always called strictly after hyperliquidConfirmAgent (never with an
 * unverified address). wallet_connected reflects stored registry state,
 * not this call's own success.
 */
export async function hyperliquidListSubaccounts(
  payload: HyperliquidListSubaccountsRequest
): Promise<HyperliquidListSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      hyperliquidListSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.hyperliquidListSubaccountsResponse as HyperliquidListSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Batch-link sub-accounts to new sibling OrderX accounts. Zero wallet
 * signatures. Per-slot failures surface in the response
 * (HyperliquidLinkedSlot.error), not as a rejected promise — only a
 * transport/auth-level failure rejects.
 */
export async function hyperliquidLinkSubaccounts(
  payload: HyperliquidLinkSubaccountsRequest
): Promise<HyperliquidLinkSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      hyperliquidLinkSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.hyperliquidLinkSubaccountsResponse as HyperliquidLinkSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Mint a Derive session key and return the unsigned `set_session_key` Action
 * as an `eth_signTypedData_v4` payload. `walletAddress` is REQUIRED: it is the
 * Action's owner and signer, so the wallet must be connected first. Single-use,
 * expires after 30 minutes — call this immediately before prompting the wallet,
 * never at page load.
 *
 * `typedDataJson` goes to the wallet verbatim and `chainId` names the network
 * its domain separator binds to (the wallet must be on that chain to sign).
 * `matchingContractAddress`, `needsSubaccount` and `userOpHash` are vestigial —
 * kept on the wire, read by nothing (see the proto doc comments).
 */
export async function derivePrepareSessionKey(
  payload: DerivePrepareSessionKeyRequest
): Promise<DerivePrepareSessionKeyResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      derivePrepareSessionKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.derivePrepareSessionKeyResponse as DerivePrepareSessionKeyResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Submit the wallet's typed-data signature over the prepared Action, plus the
 * `personal_sign` login signature that authenticates the registration call.
 * The backend recovers the signer against the Action it itself persisted,
 * never against anything sent here, then registers the key with Derive and
 * activates the account. Errors (expired session / signature mismatch /
 * venue rejection) surface verbatim.
 *
 * The deprecated `registrationTxHash`, `subaccountId` and `userOpSignature`
 * fields are omitted from the payload type: nothing reads them, and sending
 * placeholder values would read like data.
 */
export async function deriveConfirmSessionKey(
  payload: Omit<
    DeriveConfirmSessionKeyRequest,
    'registrationTxHash' | 'subaccountId' | 'userOpSignature'
  >
): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      deriveConfirmSessionKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * List a Derive wallet's subaccounts for the connection picker, on a
 * `personal_sign` login header the wallet produces itself. That header reads
 * as the owner, so the listing covers the whole wallet and needs no session
 * key — which is what lets the customer choose before anything is registered.
 * A stored key would answer too, but only for the subaccounts it was
 * registered over, and the picker has to offer the rest.
 *
 * An empty list means "nothing here yet": the customer has to deposit on
 * derive.xyz before anything can be linked.
 */
export async function deriveListSubaccounts(
  payload: DeriveListSubaccountsRequest
): Promise<DeriveListSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      deriveListSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.deriveListSubaccountsResponse as DeriveListSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Batch-link subaccounts to OrderX accounts. Zero wallet signatures: the
 * stored session key covers everything. If the initiating account has no
 * subaccount yet it takes the first slot; every further slot spawns a
 * sibling OrderX account (proxy + DDB row) linked to its subaccount.
 * `createCount` fresh subaccounts are created via Derive's API first. Per-slot
 * failures surface in the response (`DeriveLinkedSlot.ok`/`error`), not as a
 * rejected promise — only a transport/auth-level failure rejects.
 */
export async function deriveLinkSubaccounts(
  payload: DeriveLinkSubaccountsRequest
): Promise<DeriveLinkSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      deriveLinkSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.deriveLinkSubaccountsResponse as DeriveLinkSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Owner-gated unlink of a Derive session-key account. The backend clears the
 * stored session key and releases the master-wallet reservation. This does
 * NOT deregister the key at Derive — only the customer's own wallet can do
 * that. Errors surface verbatim.
 */
export async function deriveDisconnect(payload: DeriveDisconnectRequest): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      deriveDisconnect: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * List a Lighter L1 wallet's subaccounts for the connection picker. A
 * zero-length `subaccounts` array is the expected state for a wallet that
 * hasn't deposited to Lighter yet — render it as a prompt, not an error
 * (see docs/lighter-frontend.md §2 in the oems_v2 repo).
 */
export async function lighterListSubaccounts(
  payload: LighterListSubaccountsRequest
): Promise<LighterListSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterListSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterListSubaccountsResponse as LighterListSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Step 1 of the wallet-driven key mint: the backend generates a fresh
 * keypair server-side and returns only a human-readable text string for the
 * wallet to `personal_sign` (see lib/lighter/sign-subaccount-key.ts). The
 * generated secret never crosses this response.
 */
export async function lighterPrepareSubaccountKey(
  payload: LighterPrepareSubaccountKeyRequest
): Promise<LighterPrepareSubaccountKeyResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterPrepareSubaccountKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterPrepareSubaccountKeyResponse as LighterPrepareSubaccountKeyResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Step 2: submit the wallet's `personal_sign` signature. On success the
 * backend has registered the key with Lighter (ChangePubKey); the
 * `preparedKeyId` becomes usable as a link request's key source. On
 * failure the pending entry is left retriable — resend confirm with the
 * same `preparedKeyId` rather than restarting prepare, unless `ok: false`
 * with an expiry-style error says otherwise.
 */
export async function lighterConfirmSubaccountKey(
  payload: LighterConfirmSubaccountKeyRequest
): Promise<LighterConfirmSubaccountKeyResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterConfirmSubaccountKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterConfirmSubaccountKeyResponse as LighterConfirmSubaccountKeyResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Step 1 of the Partner Attribution consent: the backend builds and L2-signs
 * the on-chain ApproveIntegrator tx and returns the text for the wallet to
 * `personal_sign`. Also returns what is actually being approved (integrator
 * index + fee ceiling) so the UI can show it rather than asking the customer
 * to authorize an opaque blob.
 *
 * Rejects when no integrator is provisioned server-side — preparing a consent
 * that could never take effect would be worse than saying so.
 */
export async function lighterPrepareIntegratorConsent(
  payload: LighterPrepareIntegratorConsentRequest
): Promise<LighterPrepareIntegratorConsentResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterPrepareIntegratorConsent: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterPrepareIntegratorConsentResponse as LighterPrepareIntegratorConsentResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Step 2: submit the wallet's signature. On success the on-chain
 * ApproveIntegrator has landed AND the per-account flag is set, so the
 * trade-venue starts attributing this account's orders on its next instance
 * start. The flag is never set on a failed submission.
 */
export async function lighterConfirmIntegratorConsent(
  payload: LighterConfirmIntegratorConsentRequest
): Promise<LighterConfirmIntegratorConsentResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterConfirmIntegratorConsent: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterConfirmIntegratorConsentResponse as LighterConfirmIntegratorConsentResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Batch-link every resolved slot (≤5 per request). First slot links the
 * initiating account; every later slot spawns a sibling OrderX account.
 * Per-slot failures surface in the response (`LighterLinkedSlot.ok`/
 * `error`), not as a rejected promise — only a transport/auth-level failure
 * rejects.
 */
export async function lighterLinkSubaccounts(
  payload: LighterLinkSubaccountsRequest
): Promise<LighterLinkSubaccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      lighterLinkSubaccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.lighterLinkSubaccountsResponse as LighterLinkSubaccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Register a Paradigm desk's API credentials against one Deribit or Bybit OrderX account. The
 * desk id comes back immediately; `deskName` only once that desk has sent at least one
 * TAKER RFQ, so its absence is a normal brand-new-desk state, not a failure. Admin
 * access plus the `paradigm` plan capability are enforced server-side and refusals
 * ("Paradigm binding is only supported for Deribit and Bybit accounts", "Admin access to this
 * account is required", "Paradigm is not available on your plan") arrive as ordinary
 * error messages to surface verbatim.
 */
export async function paradigmConnectDesk(
  payload: ParadigmConnectDeskRequest
): Promise<ParadigmConnectDeskResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      paradigmConnectDesk: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.paradigmConnectDeskResponse as ParadigmConnectDeskResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * The connected desk's account nicknames, each cross-matched against this user's own
 * Deribit trade history so the picker can show proven candidates ahead of untraded
 * ones. A nickname with no history at all is still linkable — that's the expected state
 * for a desk that hasn't traded yet.
 */
export async function paradigmListAccounts(
  payload: ParadigmListAccountsRequest
): Promise<ParadigmListAccountsResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      paradigmListAccounts: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.paradigmListAccountsResponse as ParadigmListAccountsResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Bind a desk nickname to this OrderX account, returning the state the binding landed
 * in. This is an unconditional overwrite — there is no unlink RPC, so re-linking to a
 * different nickname is the only way to change or correct an existing binding.
 */
export async function paradigmLinkAccount(
  payload: ParadigmLinkAccountRequest
): Promise<ParadigmLinkAccountResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      paradigmLinkAccount: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.paradigmLinkAccountResponse as ParadigmLinkAccountResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Owner-gated unlink of this account's Paradigm desk binding — stricter than the
 * wizard's Admin gate, and deliberately NOT plan-gated so a lapsed plan can still
 * unwind a binding it could no longer create. Clears every binding field including the
 * desk id, so reconnecting means re-entering the desk access key and secret; the desk's
 * own credential record survives server-side, because one desk can back several
 * accounts. Registry cleanup afterwards is best-effort and invisible here — the client
 * sees a plain Success either way. Errors surface verbatim.
 */
export async function paradigmDisconnect(payload: ParadigmDisconnectRequest): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      paradigmDisconnect: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Mint a Papertrade session key and return the unsigned registration as an
 * `eth_signTypedData_v4` payload. `walletAddress` is the signer, so the wallet
 * must be connected first. Single-use, expires after 10 minutes — call this
 * immediately before prompting the wallet. `typedDataJson` goes to the wallet
 * verbatim and `chainId` names the domain's chain (HyperEVM, 999).
 */
export async function papertradePrepareSessionKey(
  payload: PapertradePrepareSessionKeyRequest
): Promise<PapertradePrepareSessionKeyResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      papertradePrepareSessionKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return data.papertradePrepareSessionKeyResponse as PapertradePrepareSessionKeyResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Submit the wallet's signature over the prepared typed data. The backend
 * recovers the signer against the payload it persisted, registers the key and
 * activates the account. Errors surface verbatim; see `classifyConfirmError`.
 */
export async function papertradeConfirmSessionKey(
  payload: PapertradeConfirmSessionKeyRequest
): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      papertradeConfirmSessionKey: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/** Owner-gated unlink of a Papertrade account; clears the stored session key. */
export async function papertradeDisconnect(payload: PapertradeDisconnectRequest): Promise<Success> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      papertradeDisconnect: payload,
    })
    if (data.error) return Promise.reject(data.error)
    return unwrapServerMessage(data)
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
